import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@economic/gaudi';
import { refreshCurrentLegacyPage } from '@economic/react-app';
import {
    SingleCustomerLedgerCardSendingInformation,
    SingleCustomerLedgerCardSendingRequest,
    SingleCustomerLedgerCardSendingResponse,
} from '../../../../../../types/internal-api';
import { InvoiceLayoutType } from '../../../../../../types/internal-api-extra';
import { RouteDisclosure } from '../../../../../shared/components/RouteDisclosure';
import internalApiClient from '../../../../../shared/util/api/internalApiClient';
import useQuery from '../../../../../shared/hooks/useQuery';
import { t, useLocalization } from '../../../../../shared/components/l10n/l10n';
import { useEmailTemplates } from '../../../invoicing/invoices/utils/useEmailTemplates';
import { emailOptionsFromTemplates } from '../../../invoicing/invoices/utils/emailOptionsFromTemplates';
import { emailTemplateFormState } from '../../../invoicing/invoices/utils/emailTemplateFormState';
import { SendSingleCustomerLedgerCardDialogFormData, SendSingleCustomerLedgerCardDialogRouteState } from '../types';
import { splitEmails } from '../../../invoicing/invoices/utils';
import { SendSingleCustomerLedgerCardDialog } from './SendSingleCustomerLedgerCardDialog';

function SendSingleCustomerLedgerCardDialogContainer() {
    const toast = useToast();
    // const location = useLocation<SendSingleCustomerLedgerCardDialogRouteState>();
    const location = useLocation();
    const navigate = useNavigate();
    const { state: routeState } = location as { state: SendSingleCustomerLedgerCardDialogRouteState };

    const { loading: loadingInformation, data, error: loadingInformationError } = useQuery<
        SingleCustomerLedgerCardSendingInformation
    >(`/sales/customer-ledger-cards/sending-information?id=${routeState?.customerId}`, null, { skip: !routeState?.customerId });

    const loadingTexts = useLocalization(textIds);
    const { data: templates, loading: loadingTemplates } = useEmailTemplates(InvoiceLayoutType.AccountStatement);

    const [sendingError, setSendingError] = useState<boolean>(false);
    const [sending, setSending] = useState<boolean>(false);

    const transformedInformation = useMemo<SendSingleCustomerLedgerCardDialogFormData | undefined>(() => {
        if (!data) {
            return undefined;
        }

        return {
            messages: [],
            emailOptions: {
                ...data,
                recipients: data.recipients.join(' '),
                recipientsCc: data.recipientsCc.join(' '),
                recipientsBcc: data.recipientsBcc.join(' '),
                showPdf: data.showAfterSending,
                saveSubject: false,
                saveBody: false,
                ...emailOptionsFromTemplates(templates),
            },

            ...emailTemplateFormState(templates),
        };
    }, [data, templates]);

    async function send(values: SendSingleCustomerLedgerCardDialogFormData) {
        try {
            setSending(true);

            const request: SingleCustomerLedgerCardSendingRequest = {
                customerId: routeState.customerId,
                heading: routeState.heading,
                text: routeState.text,
                currency: routeState.currency,
                fromDate: routeState.fromDate,
                toDate: routeState.toDate,
                onlyOpen: routeState.onlyOpen,
                layoutGroup: routeState.layoutGroup,
                subject: values.emailOptions.subject,
                body: values.emailOptions.body,
                encrypt: values.emailOptions.encrypt,
                includePdfLink: values.emailOptions.includePdfLink,
                includePdfAsAttachment: values.emailOptions.includePdfAsAttachment,
                recipients: splitEmails(values.emailOptions.recipients),
                recipientsCc: splitEmails(values.emailOptions.recipientsCc),
                recipientsBcc: splitEmails(values.emailOptions.recipientsBcc),
                saveSubject: values.emailOptions.saveSubject,
                saveBody: values.emailOptions.saveBody,
                showAfterSending: values.emailOptions.showPdf,
            };

            const result = await internalApiClient.post<SingleCustomerLedgerCardSendingResponse>(
                `/sales/customer-ledger-cards/send-single`,
                request
            );

            setSending(false);
            toast.success(t`FORM_BOOKANDSEND_CUSTOMERLEDGERCARD_TOAST_SUCCESS`);

            if (values.emailOptions.showPdf && result.data.pdfLink) {
                window.top && window.top.firebirdTask.then(() => window.top?.PopUp2(result.data.pdfLink, 500, 600));
            }

            refreshContentFrameAfterSuccess();
            hide();
        } catch (error) {
            if (error && error.response && error.response.status >= 500) {
                setSendingError(true);
            }
        }
    }

    function refreshContentFrameAfterSuccess() {
        refreshCurrentLegacyPage('content');
    }

    function hide() {
        navigate(location.pathname + location.search);
    }

    if (!routeState?.customerId) {
        return null;
    }

    return (
        <SendSingleCustomerLedgerCardDialog
            disclosure={<RouteDisclosure routeTrigger="#send-single-ledger-card" />}
            loading={loadingInformation || loadingTexts || loadingTemplates || sending}
            information={transformedInformation}
            onSend={send}
            onHide={hide}
            error={sendingError || !!loadingInformationError}
        />
    );
}

const textIds = [
    'SHARED_FORM_OK',
    'FORM_BOOKANDSEND_CUSTOMERLEDGERCARD_ERRORS_HEADER_CANNOTSEND',
    'FORM_BOOKANDSEND_CUSTOMERLEDGERCARD_HEADER',
    'FORM_BOOKANDSEND_CUSTOMERLEDGERCARD_SINGLE_TEXT',
    'FORM_BOOKANDSEND_CUSTOMERLEDGERCARD_SEND',
    'FORM_BOOKANDSEND_CUSTOMERLEDGERCARD_TOAST_SUCCESS',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_OPEN',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_RECIPIENTS',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_TOGGLE_CC_BCC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BCC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_SUBJECT',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BODY',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_REMEMBER',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OPEN',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_ATTACHMENT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_ENCRYPT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_LINK',
    'FORM_BOOKANDSEND_BOOK_TEXT',
    'FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR',
    'FORM_BOOKANDSEND_SELECT_EMAIL_RECIPIENT_ERROR',
    'POPUP_INVOICEETC_ERROR_INVALIDEMAIL',
    'FORM_BOOKANDSEND_CUSTOMERLEDGERCARD_SHOWAFTERSENDING',
    'FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR',
];

export default SendSingleCustomerLedgerCardDialogContainer;
